import { useEffect, useState } from 'react';

import { Tabs } from '@rockalingua/ui';
import usePaymentMethodsQuery from '@/v2/domains/Payments/queries/usePaymentMethodsQuery.hook';

import SavedCardPicker from './SavedCardPicker';
import NewCardCollector from './NewCardCollector';
import { TabContainer } from './CardSelection.styled';

export interface CardCredential {
  paymentMethodId?: number | string;
  setupIntentId?: string;
}

interface CardSelectionProps {
  confirmLabel: string;
  onConfirm: (credential: CardCredential) => Promise<void>;
  onCancel: () => void;
  footerSlot: HTMLElement | null;
  disabled?: boolean;
}

type Mode = 'existing' | 'new';

// Collecting a card, whatever it is for. It hands back either a saved payment method
// or a confirmed SetupIntent and lets the caller decide what to do with it — changing
// the default card, buying the Community add-on, whatever comes next.
export default function CardSelection({
  confirmLabel,
  onConfirm,
  onCancel,
  footerSlot,
  disabled = false,
}: CardSelectionProps) {
  const { data: paymentMethods } = usePaymentMethodsQuery();

  const [mode, setMode] = useState<Mode>('existing');

  const hasExisting = !!paymentMethods?.length;

  useEffect(() => {
    if (paymentMethods) {
      setMode(hasExisting ? 'existing' : 'new');
    }
  }, [paymentMethods]);

  const showExisting = mode === 'existing' && hasExisting;

  return (
    <>
      <TabContainer>
        {hasExisting && (
          <Tabs
            tabs={[
              { value: 'existing', label: 'Use a saved card' },
              { value: 'new', label: 'Use a new card' },
            ]}
            activeTab={mode}
            setActiveTab={(value) => setMode(value as Mode)}
          />
        )}
      </TabContainer>

      {showExisting ? (
        <SavedCardPicker
          paymentMethods={paymentMethods!}
          confirmLabel={confirmLabel}
          onConfirm={onConfirm}
          onCancel={onCancel}
          footerSlot={footerSlot}
          disabled={disabled}
        />
      ) : (
        <NewCardCollector
          confirmLabel={confirmLabel}
          onConfirm={onConfirm}
          onCancel={onCancel}
          footerSlot={footerSlot}
          disabled={disabled}
        />
      )}
    </>
  );
}
